import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api, DisclosureBar } from "./api";

const INTEREST_RANGES = ["Under $5,000", "$5,000 - $15,000", "$15,000 - $30,000", "$30,000 - $50,000", "$50,000+"];
const INVESTOR_TYPES = ["Individual", "Entity / LLC", "Trust", "Self-directed IRA", "Other"];
const ACCREDITED = ["Yes — self-reported", "No", "Not sure"];
const TIERS = ["15%", "25%", "100%", "Undecided"];

const empty = {
  first_name: "", last_name: "", email: "", phone: "", city_state: "",
  investor_type: "", entity_name: "", interest_range: "", vehicles_of_interest: [],
  preferred_tier: "", experience: "", accredited_status: "", referral_source: "", questions: "",
  consent_electronic: false, ack_confidentiality: false, ack_preliminary: false,
};

export default function RequestAccess() {
  const [form, setForm] = useState(empty);
  const [vehicles, setVehicles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/investor/overview").then(({ data }) => setVehicles((data.vehicles || []).map((v) => v.name))).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleVehicle = (name) =>
    set("vehicles_of_interest", form.vehicles_of_interest.includes(name)
      ? form.vehicles_of_interest.filter((v) => v !== name)
      : [...form.vehicles_of_interest, name]);

  const required = ["first_name", "last_name", "email", "phone", "city_state", "investor_type", "interest_range", "experience", "accredited_status"];
  const valid = required.every((k) => String(form[k]).trim()) && form.email.includes("@")
    && form.consent_electronic && form.ack_confidentiality && form.ack_preliminary;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/investor/access-request", form);
      setDone(data.request_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed — please try again");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="bg-ink" data-testid="request-success">
        <Navbar />
        <main className="grid min-h-screen place-items-center px-5 py-32">
          <div className="card-line w-full max-w-lg rounded-2xl bg-white p-10 text-center">
            <CheckCircle2 size={40} className="mx-auto text-emerald-600" />
            <h1 className="mt-5 font-display text-2xl font-extrabold text-offwhite">Access request received</h1>
            <p className="mt-3 text-sm leading-relaxed text-muted">
              Reference <span className="font-mono font-bold text-offwhite" data-testid="request-ref">{done}</span>.
              Xtreme Exotics reviews every request. If approved, you'll receive an email with next steps to create
              your portal account and complete your investor profile.
            </p>
            <DisclosureBar />
            <button onClick={() => navigate("/investors")} className="btn-ghost mt-6 w-full" data-testid="back-to-investors-btn">Back to Investor Overview</button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const Field = ({ k, label, type = "text", placeholder = "" }) => (
    <div>
      <label className="field-label" htmlFor={`ra-${k}`}>{label}</label>
      <input id={`ra-${k}`} type={type} className="field" placeholder={placeholder} value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`ra-${k}`} />
    </div>
  );

  const Select = ({ k, label, options }) => (
    <div>
      <label className="field-label" htmlFor={`ra-${k}`}>{label}</label>
      <select id={`ra-${k}`} className="field" value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`ra-${k}`}>
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <div className="bg-ink" data-testid="request-access-page">
      <Navbar />
      <main className="px-5 pb-24 pt-[140px]">
        <div className="mx-auto max-w-2xl">
          <p className="eyebrow !text-red-700">Investor Portal</p>
          <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-offwhite" data-testid="request-access-title">Request Investor Portal Access</h1>
          <p className="mt-3 text-sm leading-relaxed text-muted">
            Access is reviewed and approved by Xtreme Exotics Watersports LLC. Approval is required before any
            preliminary materials are visible. No payment information is requested at any point in this process.
          </p>
          <div className="mt-5"><DisclosureBar /></div>

          <div className="card-line mt-8 space-y-5 rounded-2xl bg-white p-7 sm:p-9">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field k="first_name" label="First name" />
              <Field k="last_name" label="Last name" />
              <Field k="email" label="Email" type="email" />
              <Field k="phone" label="Phone" type="tel" />
              <Field k="city_state" label="City & State" placeholder="St. Petersburg, FL" />
              <Select k="investor_type" label="Investor type" options={INVESTOR_TYPES} />
              <Field k="entity_name" label="Entity name (if applicable)" />
              <Select k="interest_range" label="Investment interest range" options={INTEREST_RANGES} />
              <Select k="preferred_tier" label="Preferred tier (proposed)" options={TIERS} />
              <Select k="accredited_status" label="Accredited investor status" options={ACCREDITED} />
            </div>

            <div>
              <p className="field-label">Vehicles of interest</p>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="ra-vehicles">
                {vehicles.map((v) => (
                  <button key={v} type="button" onClick={() => toggleVehicle(v)}
                    data-testid={`ra-vehicle-${v.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${form.vehicles_of_interest.includes(v) ? "border-red-700 bg-red-700 text-white" : "border-black/15 text-muted hover:border-red-700/50 hover:text-red-700"}`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="field-label" htmlFor="ra-experience">Investment experience</label>
              <textarea id="ra-experience" className="field min-h-[90px]" value={form.experience} onChange={(e) => set("experience", e.target.value)} data-testid="ra-experience" />
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field k="referral_source" label="How did you hear about us? (optional)" />
              <Field k="questions" label="Questions or comments (optional)" />
            </div>

            <div className="space-y-3 rounded-xl border border-black/[0.08] bg-surface/50 p-5">
              {[
                ["consent_electronic", "I consent to receive electronic communications from Xtreme Exotics Watersports LLC regarding this portal."],
                ["ack_confidentiality", "I acknowledge these are confidential preliminary discussion materials — no offer to sell or solicitation to buy securities is being made."],
                ["ack_preliminary", "I understand no funds are being accepted, and participation is subject to due diligence, definitive agreements, and legal approval."],
              ].map(([k, label]) => (
                <label key={k} className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-muted" data-testid={`ra-${k}-label`}>
                  <input type="checkbox" className="mt-0.5 h-4 w-4 accent-red-700" checked={form[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`ra-${k}`} />
                  {label}
                </label>
              ))}
            </div>

            <button onClick={submit} disabled={!valid || busy}
              className="w-full rounded-full bg-red-700 px-7 py-3.5 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-red-600 disabled:opacity-50"
              data-testid="ra-submit-btn">
              {busy ? <Loader2 size={15} className="mx-auto animate-spin" /> : "Submit Access Request"}
            </button>
            <p className="text-center text-[11px] text-muted/70">
              Already approved? <Link to="/investors/login" className="font-semibold text-red-700 hover:underline" data-testid="ra-login-link">Sign in to the portal</Link>
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
